// User Improvement Mode — targeted regeneration that preserves the 4-scene
// viral structure, the locked CTA format, on-screen=script alignment, and any
// fields the user manually edited.
//
// All calls go through the user's OpenAI key (gpt-4o-mini, JSON mode).

import { chatJSON } from "./openai";
import { ctaIsValid } from "./prompts";

// ────────────────────────────────────────────────────────────────────────
// Hard system prompt — applies to BOTH post-level and scene-level improvements.
// ────────────────────────────────────────────────────────────────────────
const HARD_RULES = `You are the BuddyPass AI improvement engine. The user gives you a SHORT direction (e.g. "stronger hook", "more emotional", "punchier CTA"). You apply it surgically without breaking the viral system.

CORE LAWS (do not break — output is rejected if any are violated):
1. The post is a 4-scene faceless reel: HOOK (0-3s) → CURIOSITY (3-6s) → PAYOFF (6-9s) → CTA (9-12s). 4 scenes. No more, no less. Each scene = 3 seconds. Total video = 12 seconds.
2. Per-scene word counts (spoken script): HOOK / CURIOSITY / PAYOFF = 8–12 words. CTA = 8–16 words.
3. Each scene has both \`script\` (spoken) and \`on_screen_text\` (burned-in caption). on_screen_text MUST equal the script for that scene EXACTLY, verbatim. No paraphrasing, no shortening.
4. CTA IS LOCKED to the comment-driven, book-referencing pattern. Pick from these approved templates (KEYWORD is one uppercase word):
   · "Comment {KEYWORD} and I'll send you my FREE Best-Selling Book."
   · "Want the free book? Comment {KEYWORD}."
   · "I'll send you the book if you comment {KEYWORD}."
   · "Drop a {KEYWORD} in the comments and I'll send the book over."
   The CTA MUST ask for a comment and MUST reference the FREE Best-Selling Book. Never "DM me" / "link in bio" / "click below".
5. The only deliverable allowed in the CTA is "the book" / "free book" / "FREE Best-Selling Book". NEVER promise any other asset (no spreadsheet, sheet, template, doc, link, file, attachment, pdf, ebook, workbook, guide, walkthrough, blueprint, playbook, framework, download, freebie, bonus, gift, course, training, masterclass, workshop, mini-course, checklist, cheat-sheet, swipe file).
6. image_prompt is PURELY VISUAL — describes a real cinematic photograph. Never includes text, words, captions, signage, billboards, neon signs, screen UI, app interfaces, logos, watermarks, speech bubbles, or AI text artifacts.
7. Visual progression across the 4 scenes: tension → curiosity gap → reframe → outcome. Each scene's image is distinct (no repeats).
8. Tone: real UGC voice, audience-targeted, conversational. Never marketing/ad/motivational ("transform your life", "unlock", "discover", "imagine", "ready to", "this lifestyle", "change your life", "game-changer", "level up", "empower", "take control", "POV:", "plot twist", "What if I told you…").
9. NEVER touch any field listed in \`locked_fields\` (the user manually edited those — they are the source of truth).
10. NEVER change the post.type, post.anchor_trend, post.niche_outcome, the keyword inside the CTA, or the brand niche. Only refine the requested elements.

You output STRICT JSON only — no preamble, no commentary.`;

// ────────────────────────────────────────────────────────────────────────
// POST-LEVEL improvement.
// Returns a patch object whose shape mirrors the post's editable fields.
// ────────────────────────────────────────────────────────────────────────
export async function improvePost({ apiKey, post, direction, lockedFields = [] }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  if (!post) throw new Error("Missing post");
  const userDirection = (direction || "make it more viral").toString().trim().slice(0, 400);

  const keyword = extractKeyword(post.cta) || "GO";

  const system = HARD_RULES;
  const user = `IMPROVE THIS POST.

USER DIRECTION (apply surgically, do not over-rewrite): "${userDirection}"

LOCKED_FIELDS (NEVER modify these — they are user edits): ${JSON.stringify(lockedFields)}

CTA KEYWORD (must remain exactly this single uppercase word): ${keyword}

CURRENT POST (JSON):
${JSON.stringify(strippedForPrompt(post), null, 2)}

TASK:
1. Decide internally which 1-3 elements actually need to change to honour the user direction. Touch as few fields as possible.
2. Apply changes ONLY to non-locked fields.
3. Keep all hard rules from the CORE LAWS above.
4. If you change any scene's \`script\`, automatically set its \`on_screen_text\` to match the new script verbatim, and refresh that scene's \`image_prompt\` so it emotionally matches the new line.
5. If you change the CTA, keep the comment-driven, book-referencing pattern locked to keyword "${keyword}" (one of the approved templates). Never "DM me" / "link in bio".
6. The full \`script\` field (the joined 4 sentences) must be regenerated to match the (possibly updated) scenes — Scene 4's script is always the final sentence of \`post.script\` and equals \`post.cta\`.

Return STRICT JSON with ONLY the fields you actually changed (omit unchanged fields):
{
  "changed_summary": "1 short sentence — what you improved and why (for the user)",
  "patch": {
    "hook"?: "...",
    "script"?: "...",
    "cta"?: "...",
    "caption"?: "...",
    "hashtags"?: ["#..."],
    "scenes"?: [
      // ALL 4 scenes if scenes were touched. Each scene MUST contain role, script, on_screen_text (= script verbatim), image_prompt, pexels_query, duration.
      // If scenes weren't touched, omit the "scenes" key entirely.
      { "role": "HOOK", "script": "...", "on_screen_text": "...", "image_prompt": "...", "pexels_query": "...", "duration": 3 }
    ]
  }
}
JSON only.`;

  const result = await chatJSON({ apiKey, system, user, temperature: 0.85 });
  const patch = sanitizePatch(result?.patch || {}, { keyword, lockedFields });
  return {
    summary: result?.changed_summary || "Updated.",
    patch,
  };
}

// ────────────────────────────────────────────────────────────────────────
// SCENE-LEVEL improvement.
// Returns a single scene patch (script + on_screen_text + image_prompt).
// ────────────────────────────────────────────────────────────────────────
export async function improveScene({ apiKey, post, sceneIndex, direction }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  if (!post) throw new Error("Missing post");
  if (!Array.isArray(post.scenes) || !post.scenes[sceneIndex]) {
    throw new Error("Scene not found");
  }
  const userDirection = (direction || "make it stronger").toString().trim().slice(0, 400);
  const scene = post.scenes[sceneIndex];
  const role = (scene.role || "").toUpperCase();
  const isCta = role === "CTA" || sceneIndex === 3;
  const keyword = extractKeyword(post.cta) || "GO";

  const wordRange = isCta ? "8–16" : "8–12";

  const system = HARD_RULES;
  const user = `IMPROVE A SINGLE SCENE (Scene ${sceneIndex + 1} of 4 · role=${role || "—"}).

USER DIRECTION (apply surgically): "${userDirection}"

CTA KEYWORD (only relevant for the CTA scene — must remain this single uppercase word): ${keyword}

POST CONTEXT (for grounding — DO NOT modify any other scene):
${JSON.stringify(strippedForPrompt(post), null, 2)}

CURRENT SCENE:
${JSON.stringify(scene, null, 2)}

CONSTRAINTS FOR THIS SCENE:
- Spoken script: ${wordRange} words.
- ${isCta ? `Scene 4 CTA: must be one of the approved comment-driven, book-referencing templates with keyword "${keyword}". E.g. "Comment ${keyword} and I'll send you my FREE Best-Selling Book." / "Want the free book? Comment ${keyword}." / "I'll send you the book if you comment ${keyword}." / "Drop a ${keyword} in the comments and I'll send the book over." Never DM, never link-in-bio. Never name any deliverable other than the book.` : `Tone: ${role === "HOOK" ? "pattern-interrupt, search-intent, scroll-stopping" : role === "CURIOSITY" || role === "PROBLEM" ? "open the loop — tease the hidden thing/contradiction without revealing it" : role === "PAYOFF" || role === "INSIGHT" ? "close the loop — the reframe / contradiction / hidden truth" : "real UGC voice"}.`}
- on_screen_text MUST equal the new script for this scene EXACTLY (verbatim).
- image_prompt MUST be a fresh, purely visual cinematic photograph that emotionally matches the NEW script. NO text, words, captions, signage, logos, screen UI, watermarks. Distinct from the other scenes' visuals (visual progression: tension → curiosity → reframe → outcome — Scene ${sceneIndex + 1} corresponds to "${["tension","curiosity","reframe","outcome"][sceneIndex] || "outcome"}").

Return STRICT JSON:
{
  "changed_summary": "1 short sentence — what you improved and why",
  "scene": {
    "role": "${role || ["HOOK","CURIOSITY","PAYOFF","CTA"][sceneIndex] || "HOOK"}",
    "script": "...",
    "on_screen_text": "... (must equal script verbatim)",
    "image_prompt": "...",
    "pexels_query": "same words as image_prompt, lowercase, no punctuation",
    "duration": 3
  }
}
JSON only.`;

  const result = await chatJSON({ apiKey, system, user, temperature: 0.85 });
  const newScene = sanitizeScene(result?.scene || {}, { isCta, keyword, fallback: scene });
  return {
    summary: result?.changed_summary || "Scene updated.",
    scene: newScene,
    sceneIndex,
  };
}

// ────────────────────────────────────────────────────────────────────────
// Helpers.
// ────────────────────────────────────────────────────────────────────────

// Strip noisy / non-essential fields out of the post before sending to the
// model — keeps prompts cheap and focused.
function strippedForPrompt(post) {
  const {
    type, anchor_trend, niche_outcome, specificity_marker,
    hook, script, scenes, cta, caption, hashtags,
    trending_audio_suggestion,
  } = post || {};
  return {
    type, anchor_trend, niche_outcome, specificity_marker,
    hook, script, cta, caption, hashtags,
    scenes: Array.isArray(scenes)
      ? scenes.map((s) => ({
          role: s.role,
          script: s.script || s.sentence || "",
          on_screen_text: s.on_screen_text || "",
          image_prompt: s.image_prompt || "",
          duration: s.duration ?? 3,
        }))
      : [],
    trending_audio_suggestion: trending_audio_suggestion
      ? { song: trending_audio_suggestion.song, artist: trending_audio_suggestion.artist }
      : undefined,
  };
}

function extractKeyword(cta = "") {
  // Try to pull the keyword out of either format ("If you type X" or "Comment X so you can…").
  const m1 = String(cta).match(/if you type\s+([A-Z]{2,16})\b/i);
  if (m1) return m1[1].toUpperCase();
  const m2 = String(cta).match(/comment\s+([A-Z]{2,16})\b/i);
  if (m2) return m2[1].toUpperCase();
  return null;
}

function sanitizeScene(scene, { isCta, keyword, fallback }) {
  const safe = { ...fallback, ...scene };
  // Force on_screen_text = script (verbatim), regardless of what the model returned.
  const spoken = (safe.script || "").trim();
  if (spoken) safe.on_screen_text = spoken;
  // For the CTA scene, validate the locked skeleton — fall back to the previous
  // CTA if the model violated it.
  if (isCta && !ctaIsValid(safe.script || "", keyword)) {
    safe.script = fallback.script || fallback.sentence || "";
    safe.on_screen_text = safe.script;
  }
  // Guarantee duration & role.
  safe.duration = 3;
  if (!safe.role) safe.role = fallback.role || "HOOK";
  // Ensure pexels_query exists.
  if (!safe.pexels_query && safe.image_prompt) {
    safe.pexels_query = safe.image_prompt
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .split(" ")
      .slice(0, 6)
      .join(" ");
  }
  return safe;
}

function sanitizePatch(patch, { keyword, lockedFields = [] }) {
  const out = { ...patch };

  // Drop any locked top-level fields the model touched anyway.
  for (const field of lockedFields) {
    if (typeof field === "string" && !field.includes(".")) {
      delete out[field];
    }
  }

  // Validate the CTA if present.
  if (out.cta && !ctaIsValid(out.cta, keyword)) {
    delete out.cta;
  }

  // Validate scenes array if present — must be exactly 4, with on_screen_text=script.
  if (Array.isArray(out.scenes)) {
    if (out.scenes.length !== 4) {
      delete out.scenes;
    } else {
      out.scenes = out.scenes.map((s, i) => {
        const sceneLockKey = `scenes[${i}]`;
        if (lockedFields.includes(sceneLockKey)) return null; // marker — caller skips
        const role = ["HOOK", "PROBLEM", "INSIGHT", "CTA"][i];
        const isCta = i === 3;
        return sanitizeScene(
          { ...s, role: s.role || role },
          { isCta, keyword, fallback: { role, script: s.script || "", duration: 3 } }
        );
      });
    }
  }

  return out;
}
